import type React from "react";
import { Skeleton } from "@planta-v2/pds-ui/skeleton";

/**
 * Placeholder for the whole directory while the first payload lands.
 *
 * The chrome mirrors what replaces it, block for block — EventHeader,
 * UnderlineTabsList and the search field — so the page does not shift when the
 * data arrives. The directory is the landing screen, so that shift is the first
 * thing a participant sees.
 *
 * - NEVER add animate-pulse to parent div containers
 * - Skeleton already animates itself; nesting the animation double-runs it
 */
interface DirectoryPageSkeletonProps {
  /**
   * Search belongs to the All tab only — the other three hold a short,
   * curated list. Passed in because the placeholder has to make the same
   * choice the loaded page will, or the field appears and then vanishes.
   */
  readonly showSearch: boolean;
}

export function DirectoryPageSkeleton({
  showSearch,
}: DirectoryPageSkeletonProps): React.JSX.Element {
  return (
    <div className="flex flex-col">
      {/* EventHeader: px-4 pt-4 pb-4, a 15px mark over the title, then the event name; h-10 avatar */}
      <div className="flex items-start gap-3 px-4 pt-4 pb-4">
        <div className="flex min-w-0 flex-1 flex-col">
          <Skeleton className="mb-2 h-[15px] w-10" />
          <Skeleton className="h-8 w-44" />
          <Skeleton className="mt-1 h-3 w-28" />
        </div>
        <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
      </div>

      {/* UnderlineTabsList: h-8 px-3 with the hairline underneath */}
      <div className="flex h-8 shrink-0 items-center gap-2 border-b border-border px-3">
        <Skeleton className="h-3.5 w-20" />
        <Skeleton className="h-3.5 w-8" />
        <Skeleton className="h-3.5 w-16" />
        <Skeleton className="h-3.5 w-20" />
      </div>

      {/* Search: the wrapper is px-4 pt-2 pb-4 and the field h-[38px] rounded-[8px] */}
      {showSearch && (
        <div className="px-4 pt-2 pb-4">
          <Skeleton className="h-11 w-full rounded-md" />
        </div>
      )}

      {/* List rows unchanged */}
      <div className="divide-y divide-foreground/10">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3 h-16">
            <Skeleton className="h-10 w-10 rounded-full shrink-0" />
            <div className="flex flex-col gap-1 flex-1">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
